'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useMediaSource, type MediaItem } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ContentRail } from '../ui/ContentRail';

export interface SeriesModuleRailProps {
  title?: string;
  source?: string;
  limit?: number;
  seriesHrefTemplate?: string;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function SeriesModuleRail({
  title,
  source: rawSource,
  limit: rawLimit,
  seriesHrefTemplate = '/tube/series/{slug}',
}: SeriesModuleRailProps) {
  const { t } = useTranslation('site-builder-blocks');
  const media = useMediaSource();
  const source = (rawSource ?? '').trim() || 'editor-picks';
  const limit = clamp(
    typeof rawLimit === 'number' && Number.isFinite(rawLimit) ? rawLimit : 8,
    1,
    24,
  );

  const state = useAsyncResource<MediaItem[]>(
    async (signal) => {
      if (!media) return null;
      const res = await media.series.fetchSource({ source, limit, signal });
      return res && res.items.length > 0 ? res.items : null;
    },
    [media, source, limit],
  );

  if (!media) {
    return (
      <SectionShell title={title}>
        <AdapterPlaceholder blockType="SeriesModuleRail" adapterName="MediaSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={title}>
        <SkeletonRow count={limit} layout="rail" aspect="2 / 3" minCardWidth={200} />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={title}>
        <EmptyState title={t('seriesRail.empty.title')} hint={t('seriesRail.empty.hint')} />
      </SectionShell>
    );
  }

  return (
    <SectionShell title={title}>
      <ContentRail ariaLabel={title || t('shared.label.series')} itemWidth="220px" snap="start">
        {state.data.map((item) => (
          <Link
            key={item.id}
            href={seriesHrefTemplate.replace('{slug}', encodeURIComponent(item.slug))}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              aria-hidden
              style={{
                aspectRatio: '2 / 3',
                borderRadius: 'var(--radius-md, 8px)',
                background: item.imageUrl
                  ? `url(${item.imageUrl}) center/cover no-repeat`
                  : 'var(--color-neutral-200, #e4e4e7)',
              }}
            />
            <div
              style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600, lineHeight: 1.3 }}
            >
              {item.title}
            </div>
          </Link>
        ))}
      </ContentRail>
    </SectionShell>
  );
}
